import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Select,
  NativeSelect,
  Button,
  Group,
  Text,
  Loader,
  Alert,
  Divider,
  Badge,
  Switch,
  NumberInput,
  PasswordInput,
  Skeleton,
  SegmentedControl,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconLock } from '@tabler/icons-react';
import { api } from '@/services/api';
import { ServiceHealthWarning } from '@/components/ServiceHealthWarning';

interface DynamicVariable {
  name: string;
  type: 'namespace' | 'domain-extdns' | 'version' | 'text' | 'secret' | 'number' | 'boolean';
  required: boolean;
  description: string;
  value?: string | number | boolean;
  placeholder?: string;
  default?: string | number | boolean;
  min?: number;
  max?: number;
  subdomainPlaceholder?: string;
  availableDomains?: string[];
}

interface CreateEnvironmentForm {
  name: string;
  description: string;
  ttl: string;
  expiresAt: Date | null;
  repoOwner: string;
  repoName: string;
  branch: string;
  appVersion: string;
  dynamicVars: Record<string, any>;
}

export function CreateEnvironmentPage() {
  const navigate = useNavigate();
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [dynamicVariables, setDynamicVariables] = useState<DynamicVariable[]>([]);
  const [ttlMode, setTtlMode] = useState<'duration' | 'date'>('duration');

  // Async search states
  const [repoSearchValue, setRepoSearchValue] = useState('');
  const [repoOptions, setRepoOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);

  const [branchSearchValue, setBranchSearchValue] = useState('');
  const [branchOptions, setBranchOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Form with validation
  const form = useForm<CreateEnvironmentForm>({
    initialValues: {
      name: '',
      description: '',
      ttl: '7d',
      expiresAt: null,
      repoOwner: '',
      repoName: '',
      branch: '',
      appVersion: 'latest',
      dynamicVars: {},
    },
    validate: {
      name: (value: string) => {
        if (!value) return 'Name is required';
        if (!/^[a-z0-9-]+$/.test(value)) return 'Name must be lowercase alphanumeric with hyphens';
        if (value.length > 50) return 'Name must be 50 characters or less';
        return null;
      },
      description: (value: string) => {
        if (value && value.length > 500) return 'Description must be 500 characters or less';
        return null;
      },
      repoOwner: (value: string) => (!value ? 'Repository owner is required' : null),
      repoName: (value: string) => (!value ? 'Repository name is required' : null),
      branch: (value: string) => (!value ? 'Branch is required' : null),
      dynamicVars: (values: Record<string, any>) => {
        for (const variable of dynamicVariables) {
          if (variable.required && !values[variable.name]) {
            return `${variable.name} is required`;
          }
          // Validate domain-extdns subdomain
          if (variable.type === 'domain-extdns' && variable.required && !values[`${variable.name}_subdomain`]) {
            return `Subdomain is required for ${variable.name}`;
          }
        }
        return null;
      },
      expiresAt: (value: Date | null) => {
        if (ttlMode === 'date' && !value) {
          return 'Please select an expiration date';
        }
        return null;
      },
    },
  });

  // Auto-populate namespace when name changes
  useEffect(() => {
    if (form.values.name) {
      form.setFieldValue('dynamicVars.namespace', `pk8s-${form.values.name}`);
    }
  }, [form.values.name]);

  // Auto-populate version when appVersion changes
  useEffect(() => {
    if (form.values.appVersion && dynamicVariables.length > 0) {
      // Find the version-type variable (usually named 'app_version' or 'version')
      const versionVar = dynamicVariables.find(v => v.type === 'version');
      if (versionVar) {
        form.setFieldValue(`dynamicVars.${versionVar.name}`, form.values.appVersion);
      }
    }
  }, [form.values.appVersion, dynamicVariables]);

  // Debounced repository search
  const handleRepoSearch = useDebouncedCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setRepoOptions([]);
      return;
    }

    setLoadingRepos(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/github/repositories`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch repositories');
      const data = await response.json();
      
      if (!data.data || !Array.isArray(data.data)) {
        console.error('Invalid repository data:', data);
        setRepoOptions([]);
        return;
      }

      const repos = data.data as Array<{ owner: string; name: string; description?: string | null; full_name: string }>;
      
      // Filter repositories by search query
      const filtered = repos.filter((repo) => {
        if (!repo || !repo.full_name) return false;
        const searchLower = query.toLowerCase();
        const fullNameMatch = repo.full_name.toLowerCase().includes(searchLower);
        const descriptionMatch = repo.description ? repo.description.toLowerCase().includes(searchLower) : false;
        return fullNameMatch || descriptionMatch;
      });

      setRepoOptions(
        filtered.map((repo) => ({
          value: repo.full_name,
          label: repo.full_name,
          description: repo.description || '',
        }))
      );
    } catch (error) {
      console.error('Failed to fetch repositories:', error);
      setRepoOptions([]);
    } finally {
      setLoadingRepos(false);
    }
  }, 300);

  // Debounced branch search
  const handleBranchSearch = useDebouncedCallback(async (query: string) => {
    if (!form.values.repoOwner || !form.values.repoName) {
      setBranchOptions([]);
      return;
    }

    setLoadingBranches(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/github/repositories/${form.values.repoOwner}/${form.values.repoName}/branches`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch branches');
      const data = await response.json();
      const branches = data.data as Array<{ name: string; protected: boolean }>;

      // Filter branches by search query
      const filtered = query
        ? branches.filter((branch) => branch.name.toLowerCase().includes(query.toLowerCase()))
        : branches;

      setBranchOptions(
        filtered.map((branch) => ({
          value: branch.name,
          label: branch.name + (branch.protected ? ' 🔒' : ''),
        }))
      );
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      setBranchOptions([]);
    } finally {
      setLoadingBranches(false);
    }
  }, 300);

  // Load branches when repository is selected
  useEffect(() => {
    if (form.values.repoOwner && form.values.repoName) {
      handleBranchSearch('');
    }
  }, [form.values.repoOwner, form.values.repoName]);

  // Fetch releases for selected repo
  const { data: releases, isLoading: loadingReleases } = useQuery({
    queryKey: ['github-releases', form.values.repoOwner, form.values.repoName],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/github/repositories/${form.values.repoOwner}/${form.values.repoName}/releases`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch releases');
      const data = await response.json();
      return data.data as Array<{ name: string; tag_name: string }>;
    },
    enabled: !!(form.values.repoOwner && form.values.repoName),
  });

  // Fetch repository config (ephemeral-values.cue) using validate endpoint
  const {
    data: repoConfig,
    isLoading: loadingConfig,
    error: configError,
  } = useQuery({
    queryKey: ['repo-config', form.values.repoOwner, form.values.repoName, form.values.branch],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/github/repositories/${form.values.repoOwner}/${form.values.repoName}/validate?branch=${form.values.branch}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to fetch repository config');
      const data = await response.json();
      return data.data;
    },
    enabled: !!(form.values.repoOwner && form.values.repoName && form.values.branch),
  });

  // Fetch cluster metadata for external DNS domains
  const { data: clusterMetadata, isLoading: isLoadingClusterMetadata } = useQuery({
    queryKey: ['cluster-metadata'],
    queryFn: async () => {
      return await api.getClusterMetadata();
    },
  });

  // Update dynamic variables when config is loaded
  useEffect(() => {
    if (repoConfig?.runtimeVariables && !isLoadingClusterMetadata) {
      // Get external DNS domains from cluster metadata
      const externalDnsDomains = clusterMetadata?.externalDnsDomains || [];
      
      // Transform API response to our DynamicVariable interface
      const transformedVariables: DynamicVariable[] = repoConfig.runtimeVariables.map((v: any) => {
        const baseVariable = {
          name: v.varName, // Use the runtime variable name from @timoni(runtime:string:NAME)
          type: v.metadata?.inputType || v.type || 'text',
          required: v.required || false,
          description: v.metadata?.helpText || v.description,
          placeholder: v.metadata?.placeholder || v.placeholder,
          default: v.default,
        };

        // Add availableDomains for domain-extdns type
        if (baseVariable.type === 'domain-extdns') {
          return {
            ...baseVariable,
            subdomainPlaceholder: v.metadata?.placeholder || 'my-app',
            availableDomains: externalDnsDomains,
          };
        }

        return baseVariable;
      });
      
      setDynamicVariables(transformedVariables);
      
      // Initialize form values with defaults
      const initialDynamicVars: Record<string, any> = {};
      transformedVariables.forEach((variable: DynamicVariable) => {
        if (variable.default !== undefined) {
          initialDynamicVars[variable.name] = variable.default;
        }
        if (variable.type === 'namespace' && form.values.name) {
          initialDynamicVars[variable.name] = `pk8s-${form.values.name}`;
        }
        if (variable.type === 'version' && form.values.appVersion) {
          initialDynamicVars[variable.name] = form.values.appVersion;
        }
      });
      
      form.setFieldValue('dynamicVars', { ...form.values.dynamicVars, ...initialDynamicVars });
    }
  }, [repoConfig, clusterMetadata, isLoadingClusterMetadata]);

  // Create environment mutation
  const createMutation = useMutation({
    mutationFn: async (values: CreateEnvironmentForm) => {
      // Filter out helper fields (_subdomain, _domain) from runtime variables
      const runtimeVariables: Record<string, any> = {};
      Object.entries(values.dynamicVars).forEach(([key, value]) => {
        // Skip helper fields like _subdomain and _domain
        if (key.endsWith('_subdomain') || key.endsWith('_domain')) {
          return;
        }
        // Use variable names as-is from the CUE file
        runtimeVariables[key] = value;
      });

      // Build payload based on TTL mode
      const payload: any = {
        name: values.name,
        repoUrl: `https://github.com/${values.repoOwner}/${values.repoName}`,
        branch: values.branch,
        appVersion: values.appVersion,
        runtimeVariables,
      };

      // Add either expiresAt (specific date) or ttl (duration)
      if (ttlMode === 'date' && values.expiresAt) {
        // Send the specific expiration date to backend
        payload.expiresAt = values.expiresAt.toISOString();
      } else {
        // Send the duration string
        payload.ttl = values.ttl;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev'}/api/environments`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        console.error('[Create Environment] Error response:', error);
        console.error('[Create Environment] Full error object:', JSON.stringify(error, null, 2));
        console.error('[Create Environment] Sent payload:', JSON.stringify(payload, null, 2));
        console.error('[Create Environment] RuntimeVariables:', payload.runtimeVariables);
        throw new Error(error?.error?.message || error?.message || 'Failed to create environment');
      }

      const data = await response.json();
      return data.data;
    },
    onSuccess: (data) => {
      notifications.show({
        title: 'Success',
        message: `Environment "${data.name}" created successfully`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      navigate(`/environments/${data.id}`);
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create environment',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    createMutation.mutate(values);
  });

  // Release options
  const releaseOptions = [
    { value: 'latest', label: 'Latest Release' },
    ...(releases
      ?.filter((release) => release?.tag_name)
      .map((release) => ({
        value: release.tag_name,
        label: release.name || release.tag_name,
      })) ?? []),
  ];

  // Render dynamic form field based on variable type
  const renderDynamicField = (variable: DynamicVariable) => {
    const key = `dynamicVars.${variable.name}`;
    const fieldProps = {
      label: (
        <Group gap="xs">
          <Text>{variable.name}</Text>
          <Badge size="xs" variant="light">
            {variable.type}
          </Badge>
        </Group>
      ),
      description: variable.description,
      required: variable.required,
    };

    switch (variable.type) {
      case 'namespace':
        return (
          <TextInput
            {...fieldProps}
            value={form.values.dynamicVars[variable.name] || ''}
            readOnly
            leftSection={<IconLock size={16} />}
            styles={{ input: { backgroundColor: 'var(--mantine-color-gray-0)' } }}
          />
        );

      case 'version':
        return (
          <TextInput
            {...fieldProps}
            value={form.values.dynamicVars[variable.name] || ''}
            readOnly
            leftSection={<IconLock size={16} />}
            styles={{ input: { backgroundColor: 'var(--mantine-color-gray-0)' } }}
          />
        );

      case 'domain-extdns':
        const subdomain = form.values.dynamicVars[`${variable.name}_subdomain`] || '';
        const domain = form.values.dynamicVars[`${variable.name}_domain`] || variable.availableDomains?.[0];
        
        return (
          <Stack gap="xs">
            <Group gap="xs">
              <Text size="sm" fw={500}>
                {variable.name}
                {variable.required && <Text component="span" c="red"> *</Text>}
              </Text>
              <Badge size="xs" variant="light">
                {variable.type}
              </Badge>
            </Group>
            {variable.description && (
              <Text size="xs" c="dimmed">
                {variable.description}
              </Text>
            )}
            <TextInput
              placeholder={variable.subdomainPlaceholder || 'my-app'}
              value={subdomain}
              onChange={(e) => {
                form.setFieldValue(`dynamicVars.${variable.name}_subdomain`, e.currentTarget.value);
                form.setFieldValue(
                  `dynamicVars.${variable.name}`,
                  `${e.currentTarget.value}.${domain}`
                );
              }}
              rightSection={
                <NativeSelect
                  data={variable.availableDomains || []}
                  value={domain}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    form.setFieldValue(`dynamicVars.${variable.name}_domain`, value);
                    form.setFieldValue(
                      `dynamicVars.${variable.name}`,
                      `${subdomain}.${value}`
                    );
                  }}
                  rightSectionWidth={28}
                  styles={{
                    input: {
                      fontWeight: 500,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      width: 200,
                      marginRight: -2,
                      border: 'none',
                      backgroundColor: 'transparent',
                    },
                  }}
                />
              }
              rightSectionWidth={200}
              error={form.errors.dynamicVars}
            />
            <Text size="xs" c="dimmed">
              Full domain: {subdomain}.{domain}
            </Text>
          </Stack>
        );

      case 'secret':
        return (
          <PasswordInput
            {...fieldProps}
            placeholder={variable.placeholder}
            value={form.values.dynamicVars[variable.name] || ''}
            onChange={(e) => form.setFieldValue(key, e.currentTarget.value)}
          />
        );

      case 'number':
        return (
          <NumberInput
            {...fieldProps}
            placeholder={variable.placeholder}
            value={form.values.dynamicVars[variable.name] ?? variable.default}
            onChange={(value) => form.setFieldValue(key, value)}
            min={variable.min}
            max={variable.max}
          />
        );

      case 'boolean':
        return (
          <Switch
            {...fieldProps}
            checked={form.values.dynamicVars[variable.name] ?? variable.default ?? false}
            onChange={(e) => form.setFieldValue(key, e.currentTarget.checked)}
          />
        );

      case 'text':
      default:
        return (
          <TextInput
            {...fieldProps}
            placeholder={variable.placeholder}
            value={form.values.dynamicVars[variable.name] || ''}
            onChange={(e) => form.setFieldValue(key, e.currentTarget.value)}
          />
        );
    }
  };

  return (
    <Container size="md" py="md">
      <Stack gap="md">
        <div>
          <Title order={2}>Create Environment</Title>
          <Text size="sm" c="dimmed">
            Deploy a new ephemeral environment from a GitHub repository
          </Text>
        </div>

        <ServiceHealthWarning
          services={['queue', 'kubernetes']}
          message="Environment provisioning may be unavailable"
        />

        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            {/* Basic Info Section */}
            <Paper withBorder p="md">
              <Stack gap="md">
                <Text fw={500} size="lg">
                  Basic Information
                </Text>

                <TextInput
                  label="Name"
                  placeholder="my-environment"
                  description="Lowercase alphanumeric with hyphens, max 50 characters"
                  required
                  {...form.getInputProps('name')}
                />

                <Textarea
                  label="Description"
                  placeholder="Optional description for this environment"
                  description="Max 500 characters"
                  minRows={3}
                  maxRows={6}
                  {...form.getInputProps('description')}
                />

                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Expiration *
                  </Text>
                  <SegmentedControl
                    value={ttlMode}
                    onChange={(value) => setTtlMode(value as 'duration' | 'date')}
                    data={[
                      { label: 'Duration', value: 'duration' },
                      { label: 'Specific Date', value: 'date' },
                    ]}
                    mb="xs"
                    fullWidth
                  />
                  {ttlMode === 'duration' ? (
                    <Select
                      placeholder="Select duration"
                      description="How long until the environment expires"
                      required
                      data={[
                        { value: '1h', label: '1 hour' },
                        { value: '4h', label: '4 hours' },
                        { value: '24h', label: '24 hours' },
                        { value: '7d', label: '7 days' },
                        { value: '30d', label: '30 days' },
                      ]}
                      {...form.getInputProps('ttl')}
                    />
                  ) : (
                    <DatePickerInput
                      placeholder="Pick expiration date"
                      description="When the environment should expire"
                      required
                      clearable
                      minDate={new Date()}
                      {...form.getInputProps('expiresAt')}
                    />
                  )}
                </div>
              </Stack>
            </Paper>

            <Divider />

            {/* Repository Config Section */}
            <Paper withBorder p="md">
              <Stack gap="md">
                <Text fw={500} size="lg">
                  Repository Configuration
                </Text>

                <Select
                  label="Repository"
                  placeholder="Type to search repositories..."
                  description="GitHub repository to deploy from (min 2 characters)"
                  required
                  searchable
                  searchValue={repoSearchValue}
                  onSearchChange={(value) => {
                    setRepoSearchValue(value);
                    handleRepoSearch(value);
                  }}
                  data={repoOptions}
                  rightSection={loadingRepos ? <Loader size="xs" /> : undefined}
                  nothingFoundMessage={
                    repoSearchValue.length < 2
                      ? 'Type at least 2 characters to search...'
                      : 'No repositories found'
                  }
                  value={selectedRepo}
                  onChange={(value) => {
                    setSelectedRepo(value);
                    setRepoSearchValue(''); // Clear search after selection
                    setDynamicVariables([]); // Reset dynamic variables
                    setBranchOptions([]); // Reset branch options
                    setBranchSearchValue(''); // Reset branch search
                    if (value) {
                      const [owner, name] = value.split('/');
                      form.setFieldValue('repoOwner', owner);
                      form.setFieldValue('repoName', name);
                      form.setFieldValue('branch', ''); // Reset branch
                      form.setFieldValue('appVersion', 'latest'); // Reset version
                      form.setFieldValue('dynamicVars', {}); // Reset dynamic vars
                    }
                  }}
                  renderOption={({ option }) => {
                    const opt = option as any;
                    return (
                      <div>
                        <Text size="sm">{option.label}</Text>
                        {opt.description && (
                          <Text size="xs" c="dimmed">
                            {opt.description}
                          </Text>
                        )}
                      </div>
                    );
                  }}
                  error={form.errors.repoOwner || form.errors.repoName}
                />

                <Select
                  label="Branch"
                  placeholder={
                    !selectedRepo
                      ? 'Select a repository first'
                      : loadingBranches
                        ? 'Loading branches...'
                        : 'Type to search branches...'
                  }
                  description="Git branch to deploy"
                  required
                  searchable
                  searchValue={branchSearchValue}
                  onSearchChange={(value) => {
                    setBranchSearchValue(value);
                    handleBranchSearch(value);
                  }}
                  data={branchOptions}
                  rightSection={loadingBranches ? <Loader size="xs" /> : undefined}
                  disabled={!selectedRepo}
                  nothingFoundMessage="No branches found"
                  value={form.values.branch}
                  onChange={(value) => {
                    form.setFieldValue('branch', value || '');
                    setBranchSearchValue(''); // Clear search after selection
                    setDynamicVariables([]); // Reset dynamic variables when branch changes
                    form.setFieldValue('dynamicVars', {}); // Reset dynamic vars
                  }}
                  error={form.errors.branch}
                />

                <Select
                  label="App Version"
                  placeholder={loadingReleases ? 'Loading releases...' : 'Select a version'}
                  description="Application version to deploy"
                  searchable
                  data={releaseOptions.length > 0 ? releaseOptions : [{ value: 'latest', label: 'Latest Release' }]}
                  rightSection={loadingReleases ? <Loader size="xs" /> : undefined}
                  disabled={!selectedRepo || loadingReleases}
                  {...form.getInputProps('appVersion')}
                />
              </Stack>
            </Paper>

            {/* Dynamic Variables Section */}
            {form.values.branch && (
              <>
                <Divider />
                <Paper withBorder p="md">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        Runtime Variables
                      </Text>
                      {loadingConfig && <Loader size="sm" />}
                    </Group>

                    {loadingConfig && (
                      <Stack gap="md">
                        <Skeleton height={60} />
                        <Skeleton height={60} />
                        <Skeleton height={60} />
                      </Stack>
                    )}

                    {configError && (
                      <Alert icon={<IconAlertCircle />} title="Configuration Error" color="yellow">
                        Failed to load repository configuration. You can still create the environment without
                        custom variables.
                      </Alert>
                    )}

                    {!loadingConfig && dynamicVariables.length > 0 && (
                      <Stack gap="md">
                        {dynamicVariables.map((variable) => (
                          <div key={variable.name}>{renderDynamicField(variable)}</div>
                        ))}
                      </Stack>
                    )}

                    {!loadingConfig && dynamicVariables.length === 0 && !configError && (
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        No custom variables defined for this repository
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </>
            )}

            {/* Actions */}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => navigate('/environments')}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!form.isValid() || loadingConfig}
              >
                Create Environment
              </Button>
            </Group>

            {/* Error Alert */}
            {createMutation.isError && (
              <Alert icon={<IconAlertCircle />} title="Error" color="red">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : 'Failed to create environment'}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
